import { useMemo } from "react";

import { useQuery } from "@apollo/client";
import { applyAwsSystemFilterPolicy } from "utils/filter/awsSystemFilters";

import { QUERY_SAVED_FILTERS } from "api/savedFilters";

import { EntityKind } from "models/entityKind";
import { SavedFilterModel } from "models/filter";

import { useSelectedProject } from "components/project/projects/hooks";

/**
 * The project saved-filter list, as the AWS build should see it.
 *
 * EVA-257 reshapes the system filters where the list is fetched, so that every
 * reader downstream gets the right body without knowing the rule. This query
 * had three separate callers -- the project Filters panel, clearing a View and
 * changing a View -- each running the same document and each parsing a chosen
 * row straight into setFilterState. Reshaping at three call sites would leave
 * the next caller to remember; there is one door now, so it cannot.
 *
 * Returns the Apollo result untouched alongside `savedFilters`, which is the
 * reshaped list callers should read instead of `data.savedFilters`.
 */
export function useProjectSavedFiltersQuery(entityKind?: EntityKind) {
  const { selectedProject: project } = useSelectedProject();

  const result = useQuery(QUERY_SAVED_FILTERS, {
    variables: {
      req: {
        projectID: project?.projectId,
        ...(entityKind ? { entityKind } : {})
      }
    },
    context: {
      clientName: "saved-filters"
    },
    skip: !project?.projectId
  });

  const savedFilters = useMemo(
    () =>
      (applyAwsSystemFilterPolicy(
        result.data?.savedFilters as Maybe<SavedFilterModel[]>
      ) ?? []) as SavedFilterModel[],
    [result.data]
  );

  return { ...result, savedFilters };
}
